import { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import {
  addDays,
  endOfToday,
  formatISO,
  startOfToday,
  subYears,
} from 'date-fns';

import { DashboardWidgetType } from 'src/constants/dashboard';
import type { DashboardResultOverview } from 'src/types/dashboard';
import type { SimpleSurveySettings, SurveyListItem } from 'src/types/survey';
import { apiPaths } from 'src/services/api/apiPaths';
import type { AggregateList } from 'src/components/followUp/common/types';
import { FETCHING_GLOBAL_SURVEY } from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

export const pathMap: Partial<{ [key in DashboardWidgetType]: string }> = {
  [DashboardWidgetType.SPEEDOMETER]: apiPaths.WIDGET_DATA.RESPONSE_RATE,
  [DashboardWidgetType.NPS_SCORE_GAUGE]: apiPaths.WIDGET_DATA.NPS_SPEEDOMETER,
};

export const useWidgetData = (
  survey: SurveyListItem,
  dashboardResults?: DashboardResultOverview,
) => {
  const currentWidgetType = dashboardResults?.widget?.widget_type;
  const path = currentWidgetType ? pathMap[currentWidgetType] : undefined;

  const extraFilters = useMemo(() => {
    const { currentTimePeriodStartDate } =
      survey.settings || ({} as SimpleSurveySettings);
    const createdAtFrom =
      currentTimePeriodStartDate ||
      formatISO(subYears(addDays(startOfToday(), 1), 1));
    const createdAtTo = formatISO(endOfToday());
    const orgUnit = `${dashboardResults?.user_root_org_unit?.id ?? ''}`;

    return { orgUnit, createdAtFrom, createdAtTo };
  }, [survey, dashboardResults]);

  const endpoint = useMemo(() => {
    if (!path || !currentWidgetType) return;

    return generatePath(path, {
      surveyId: survey.id,
      widgetId: dashboardResults?.widget?.id,
    });
  }, [dashboardResults, path, survey.id]);

  return useWidgetQuery<AggregateList>(endpoint ?? FETCHING_GLOBAL_SURVEY, {
    extraFilters,
  });
};

export default useWidgetData;
